// src/stores/authStore.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from '../types/auth';
import { logger } from '../utils/logger';

// Define application roles type
type AppRole = 'admin' | 'user' | string;

// Map Azure AD roles to application roles
const roleMapping: Record<AppRole, string[]> = {
  admin: ['TIL.PMP.ADMIN'],
  user: ['TIL.PMP.USER', 'TIL.PMP.ADMIN'] // Admin users also have user access
  // Future roles can be added here
  // manager: ['TIL.PMP.MANAGER', 'TIL.PMP.ADMIN']
};

interface AuthState {
  user: User | null;
  apiToken: string | null;
  graphToken: string | null;
  isAuthenticated: boolean;
  setAuth: (user: User, apiToken: string, graphToken?: string) => void;
  clearAuth: () => void;
  hasRole: (role: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      apiToken: null,
      graphToken: null,
      isAuthenticated: false,
      setAuth: (user, apiToken, graphToken) => set({ 
        user, 
        apiToken, 
        graphToken, 
        isAuthenticated: true 
      }),
      clearAuth: () => set({ 
        user: null, 
        apiToken: null, 
        graphToken: null, 
        isAuthenticated: false 
      }),
      // Flexible role checking with hierarchy
      hasRole: (role: string) => {
        const { user } = get();
        if (!user) {
          logger.debug(`hasRole check failed: no user`);
          return false;
        }

        logger.debug(`Checking if user has role: ${role}`);
        logger.debug(`User object: ${JSON.stringify({
          role: user.role,
          roles: user.roles
        })}`);

        // First check the user's application role with role hierarchy
        if (roleMapping[role]) {
          // If user has Azure AD roles, check those first
          if (user.roles && user.roles.length > 0) {
            logger.debug(`User has Azure roles: ${user.roles.join(', ')}`);
            logger.debug(`Checking against mapped roles: ${roleMapping[role].join(', ')}`);
            
            // Check if user has any of the Azure roles mapped to this app role
            const hasRole = roleMapping[role].some((azureRole: string) => 
              user.roles?.includes(azureRole)
            );
            
            logger.debug(`User ${hasRole ? 'has' : 'does not have'} role ${role} based on Azure roles`);
            return hasRole;
          }
          
          // Fall back to the simple role property
          logger.debug(`No Azure roles found, falling back to user.role property: ${user.role}`);
          if (role === 'admin') {
            const isAdmin = user.role === 'admin';
            logger.debug(`User ${isAdmin ? 'is' : 'is not'} admin based on user.role`);
            return isAdmin;
          }
          if (role === 'user') {
            const isUser = user.role === 'user' || user.role === 'admin';
            logger.debug(`User ${isUser ? 'is' : 'is not'} user based on user.role`);
            return isUser;
          }
        }
        
        // Direct role check (for checking against raw Azure AD roles)
        if (user.roles && user.roles.length > 0) {
          const hasDirectRole = user.roles.includes(role);
          logger.debug(`Direct role check for ${role}: ${hasDirectRole}`);
          return hasDirectRole;
        }
        
        logger.debug(`Role check for ${role} failed: no matching criteria`);
        return false;
      },
    }),
    {
      name: 'auth-storage',
    }
  )
);